import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import OfferBNPLAd from '../OfferBNPLAd'
import BundleAndSaveUSPs from './BundleAndSaveUSPs'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ShareButton from 'components/Common/ShareWithFriendsButton'
import BundleAndSaveSummary from './BundleAndSaveSummary'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { shouldShowSalesUSP } from 'selectors/featuresSelectors'
import ContactUSP from '../ContactUSP'
import { nonNullable } from 'lib/array/arrayUtils'
import BundleAndSavePropertyDetails from './BundleAndSavePropertyDetails'
import { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import Group from 'components/utils/Group'
import { OFFER_VIEWS_THRESHOLD } from 'constants/offer'
import OfferViewsUrgencyBanner from '../OfferViewsUrgencyBanner'
import OfferPageLabelGroup from '../OfferPageLabelGroup'

const OfferIntroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr min-content;
    grid-gap: ${rem(16)} ${rem(48)};
  }
`

const ContentColumn = styled.div`
  > * + * {
    margin-top: ${rem(16)};
  }
`

const SummaryColumn = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    flex-direction: column;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const SummaryBlock = styled.div`
  ${mediaQueryUp.tablet} {
    position: sticky;
    top: ${rem(12)};
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const HotelSummary = styled(BundleAndSaveSummary)`
  min-width: ${rem(308)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(382)};
  }
`

const OfferIntroWrapper = styled(LayoutContainer)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(28)};
    padding-bottom: ${rem(28)};
  }
`

const MobileOfferViews = styled(OfferViewsUrgencyBanner)`
  position: relative;
  top: -${rem(20)};
  margin: 0 -${rem(20)};
`

interface Props {
  offer: App.BundleOffer;
  className?: string;
  onViewPackageOptions: () => void;
  shouldShowSalesPhoneUSP: boolean;
  shareConfig: ShareConfig;
}

function BundleAndSaveIntro(props: Props) {
  const {
    offer,
    className,
    onViewPackageOptions,
    shouldShowSalesPhoneUSP,
    shareConfig,
  } = props

  const bundledOffers = useMemo(() => Object.values(offer.bundledOffers), [offer.bundledOffers])

  // Eg. `Melbourne`, `Victoria` & `Kuta`, `Bali`
  const locationCaption = useMemo(() =>
    bundledOffers.map(offer =>
      nonNullable([
        offer.locationHeading,
        offer.locationSubheading,
      ]).join(', '))
      .join(' & ')
  , [bundledOffers])

  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])

  const showViewsCount = offer.stats.views > OFFER_VIEWS_THRESHOLD

  return (
    <OfferIntroWrapper>
      {showViewsCount && <CSSBreakpoint max="mobile">
        <MobileOfferViews offer={offer} />
      </CSSBreakpoint> }
      <OfferIntroGrid className={className} data-testid="offer-intro">
        <ContentColumn>
          <OfferPageLabelGroup offer={offer} />
          <BundleAndSavePropertyDetails
            offer={offer}
            locationCaption={locationCaption}
            bundledOffers={bundledOffers}
          />
          <BundleAndSaveUSPs offer={offer} />
        </ContentColumn>
        <SummaryColumn>
          <CSSBreakpoint min="tablet">
            <Group direction="horizontal" verticalAlign="center" gap={8} horizontalAlign="end">
              <ShareModal shareConfig={shareConfig}>
                <ShareButton horizontalOutdent="end" />
              </ShareModal>
            </Group>
          </CSSBreakpoint>
          <SummaryBlock>
            <div>
              {showViewsCount && <OfferViewsUrgencyBanner offer={offer} /> }
              {offer.packages.length > 0 && (
                <HotelSummary
                  onViewPackageOptions={onViewPackageOptions}
                />
              )}
            </div>
            <OfferBNPLAd prices={allPrices} />
          </SummaryBlock>
        </SummaryColumn>
        {shouldShowSalesPhoneUSP && <ContactUSP />}
      </OfferIntroGrid>
    </OfferIntroWrapper>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  shouldShowSalesPhoneUSP: shouldShowSalesUSP(appState, state.offer?.lowestPricePackage?.trackingPrice, 1000),
  offer: state.offer,
  regionCode: appState.geo.currentRegionCode,
  pathname: appState.router.location.pathname,
  customerPortalURL: appState.system.customerPortalURL,
  memberId: appState.auth.account.memberId,
}))(BundleAndSaveIntro)
